import type {
  HeaderContent,
  HeroContent,
  FeaturesContent,
  TestimonialsContent,
  AboutContent,
  CTAContent,
  FooterContent,
  TypedSection,
  SectionType,
} from './types';

const headerContent: HeaderContent = {
  logo: {
    text: 'CoachElite',
    href: '#',
  },
  navigation: [
    { id: crypto.randomUUID(), text: 'Services', href: '#services' },
    { id: crypto.randomUUID(), text: 'About', href: '#about' },
    { id: crypto.randomUUID(), text: 'Testimonials', href: '#testimonials' },
    { id: crypto.randomUUID(), text: 'Contact', href: '#contact' },
  ],
  mobileMenuAriaLabel: 'Toggle menu',
};

const heroContent: HeroContent = {
  title: 'Unlock Your Full Potential',
  subtitle:
    'Professional coaching to help you overcome challenges, achieve your goals, and transform your career and life.',
  cta: {
    primary: {
      text: 'Book Discovery Call',
      icon: '📅',
      href: '#discovery',
    },
    secondary: {
      text: 'Learn About Me',
      icon: '👤',
      href: '#about',
    },
  },
};

const featuresContent: FeaturesContent = {
  heading: {
    title: 'How I Can Help You',
    subtitle: 'Expert coaching designed to address your unique challenges and goals',
  },
  features: [
    {
      id: crypto.randomUUID(),
      icon: '📈',
      title: 'Career Advancement',
      description:
        "Develop the clarity, confidence, and strategy to take your career to new heights, whether you're seeking promotion, career change, or greater fulfillment.",
      link: {
        text: 'Learn more',
        href: '#',
      },
    },
    {
      id: crypto.randomUUID(),
      icon: '👥',
      title: 'Leadership Development',
      description:
        'Enhance your leadership capabilities, improve team dynamics, and build the emotional intelligence needed to inspire and motivate others.',
      link: {
        text: 'Learn more',
        href: '#',
      },
    },
    {
      id: crypto.randomUUID(),
      icon: '⚖️',
      title: 'Work-Life Balance',
      description:
        'Create a sustainable approach to success that honors your personal values, health, and relationships while achieving professional excellence.',
      link: {
        text: 'Learn more',
        href: '#',
      },
    },
  ],
};

const testimonialsContent: TestimonialsContent = {
  heading: {
    title: 'Client Success Stories',
    subtitle: "Real results from professionals who've transformed their careers through coaching",
  },
  testimonials: [
    {
      id: crypto.randomUUID(),
      quote:
        'Working with this coach helped me gain clarity on my career path and develop the confidence to pursue a leadership role I previously thought was out of reach.',
      author: {
        name: 'Sarah J.',
        role: 'Marketing Director',
        image: {
          src: '/api/placeholder/150/150',
          alt: 'Sarah J.',
        },
      },
    },
    {
      id: crypto.randomUUID(),
      quote:
        'The coaching sessions gave me actionable strategies to overcome challenges and communicate more effectively with my team, resulting in higher productivity and morale.',
      author: {
        name: 'Michael T.',
        role: 'Tech Team Lead',
        image: {
          src: '/api/placeholder/150/150',
          alt: 'Michael T.',
        },
      },
    },
    {
      id: crypto.randomUUID(),
      quote:
        'I achieved a better work-life balance without sacrificing my career goals. The personalized approach helped me create systems that work specifically for my lifestyle.',
      author: {
        name: 'Jennifer K.',
        role: 'Finance Executive',
        image: {
          src: '/api/placeholder/150/150',
          alt: 'Jennifer K.',
        },
      },
    },
  ],
};

const ctaContent: CTAContent = {
  title: 'Ready to Transform Your Career?',
  benefits: [
    {
      id: crypto.randomUUID(),
      icon: '💬',
      title: 'Personalized Insights',
      description:
        'Get clarity on your specific challenges and receive tailored advice based on your unique situation.',
    },
    {
      id: crypto.randomUUID(),
      icon: '🗺️',
      title: 'Clear Action Plan',
      description:
        'Walk away with at least one powerful strategy you can implement immediately to move forward.',
    },
    {
      id: crypto.randomUUID(),
      icon: '🛡️',
      title: 'Zero Obligation',
      description:
        'This 30-minute call is completely free with no pressure or obligation to purchase additional services.',
    },
  ],
  callToAction: {
    guarantee:
      '95% of clients report gaining valuable insights from just the initial discovery call',
    button: {
      text: 'Schedule Your Free 30-Minute Call',
      icon: '📅',
      href: '#',
    },
    availability: 'Limited spots available this month — Book now to secure your spot',
  },
};

const footerContent: FooterContent = {
  logo: {
    text: 'CoachElite',
    href: '#',
  },
  about: {
    description:
      'Professional coaching for ambitious individuals seeking meaningful career advancement and personal growth.',
    socialLinks: [
      { id: crypto.randomUUID(), icon: 'in', label: 'LinkedIn', href: '#' },
      { id: crypto.randomUUID(), icon: 'f', label: 'Facebook', href: '#' },
      { id: crypto.randomUUID(), icon: 't', label: 'Twitter', href: '#' },
      { id: crypto.randomUUID(), icon: 'ig', label: 'Instagram', href: '#' },
    ],
  },
  columns: [
    {
      id: crypto.randomUUID(),
      title: 'Services',
      links: [
        { id: crypto.randomUUID(), text: 'Career Advancement', href: '#', icon: '→' },
        { id: crypto.randomUUID(), text: 'Leadership Development', href: '#', icon: '→' },
        { id: crypto.randomUUID(), text: 'Work-Life Balance', href: '#', icon: '→' },
        { id: crypto.randomUUID(), text: 'Team Coaching', href: '#', icon: '→' },
      ],
    },
    {
      id: crypto.randomUUID(),
      title: 'Resources',
      links: [
        { id: crypto.randomUUID(), text: 'Blog', href: '#', icon: '→' },
        { id: crypto.randomUUID(), text: 'Podcast', href: '#', icon: '→' },
        { id: crypto.randomUUID(), text: 'Free Assessment', href: '#', icon: '→' },
        { id: crypto.randomUUID(), text: 'FAQ', href: '#', icon: '→' },
      ],
    },
    {
      id: crypto.randomUUID(),
      title: 'Contact',
      links: [
        {
          id: crypto.randomUUID(),
          text: 'hello@coachelite.com',
          href: 'mailto:hello@coachelite.com',
          icon: '✉️',
        },
        { id: crypto.randomUUID(), text: '(123) 456-7890', href: 'tel:+11234567890', icon: '📱' },
        { id: crypto.randomUUID(), text: 'Book a Call', href: '#', icon: '📅' },
      ],
    },
  ],
  copyright: '© 2025 CoachElite. All rights reserved.',
};

const aboutContent: AboutContent = {
  title: 'About Your Coach',
  paragraphs: [
    'With over 15 years of experience in leadership and professional development, I help ambitious professionals overcome obstacles and achieve meaningful success.',
    'My approach combines proven coaching methodologies with practical business experience to deliver results-oriented guidance tailored to your unique situation and goals.',
  ],
  image: {
    type: 'placeholder',
    dimensions: {
      width: 500,
      height: 600,
    },
  },
  credentials: [
    {
      id: crypto.randomUUID(),
      icon: '🏆',
      text: 'Certified Professional Coach (ICF)',
    },
    {
      id: crypto.randomUUID(),
      icon: '👑',
      text: 'Former Fortune 500 Executive',
    },
  ],
};

export const sampleData: TypedSection<SectionType>[] = [
  { id: crypto.randomUUID(), type: 'Header', data: headerContent },
  { id: crypto.randomUUID(), type: 'Hero', data: heroContent },
  { id: crypto.randomUUID(), type: 'Features', data: featuresContent },
  { id: crypto.randomUUID(), type: 'Testimonials', data: testimonialsContent },
  { id: crypto.randomUUID(), type: 'About', data: aboutContent },
  { id: crypto.randomUUID(), type: 'CTA', data: ctaContent },
  { id: crypto.randomUUID(), type: 'Footer', data: footerContent },
];
